import React from 'react';
import { Sparkles, Check, X } from 'lucide-react';
import { formatLastActive } from './_utils';

/**
 * ExerciseSuggestionsCard — pending custom-exercise submissions queue
 * with accept/reject actions. Stateless; parent supplies the filtered
 * pending list + handler. Extracted from Admin.jsx.
 */
const ExerciseSuggestionsCard = ({ pendingSuggestions, reviewBusy, onReview }) => (
  <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6 mb-10" data-testid="admin-suggestions-card">
    <div className="flex items-center justify-between mb-5">
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-enf-accent-fg" />
        <div className="text-sm font-semibold uppercase tracking-widest text-enf-text-secondary">Custom exercise suggestions</div>
      </div>
      <span className="text-xs text-enf-text-muted" data-testid="admin-suggestions-count">{pendingSuggestions.length} pending</span>
    </div>

    {pendingSuggestions.length === 0 ? (
      <div className="text-sm text-enf-text-muted py-6 text-center">
        No pending suggestions. Users haven't submitted any new exercises to the Enferno library — or you've cleared the queue.
      </div>
    ) : (
      <div className="space-y-3">
        {pendingSuggestions.map((s) => (
          <div
            key={s.id}
            className="bg-enf-surface-mid border border-enf-surface-border rounded-lg p-4"
            data-testid={`suggestion-row-${s.id}`}
          >
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-enf-text-primary truncate" data-testid={`suggestion-name-${s.id}`}>{s.name}</div>
                <div className="text-xs text-enf-text-muted mt-1 flex flex-wrap gap-x-3 gap-y-1">
                  {s.muscle && <span><span className="text-enf-text-muted">Muscle:</span> {s.muscle}</span>}
                  {s.user_email && <span><span className="text-enf-text-muted">From:</span> {s.user_email}</span>}
                  {s.created_at && <span><span className="text-enf-text-muted">Submitted:</span> {formatLastActive(s.created_at)}</span>}
                </div>
                {s.notes && (
                  <div className="text-xs text-enf-text-secondary mt-2 whitespace-pre-wrap leading-relaxed border-l-2 border-enf-surface-border pl-3">
                    {s.notes}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => onReview(s.id, 'accepted')}
                  disabled={reviewBusy === s.id}
                  className="px-3 py-1.5 enf-success-pill bg-emerald-600/15 hover:bg-emerald-600/25 disabled:opacity-50 border border-emerald-600/40 text-emerald-300 rounded-md text-xs font-semibold flex items-center gap-1.5 transition-colors"
                  data-testid={`suggestion-accept-${s.id}`}
                >
                  <Check className="w-3.5 h-3.5" /> Accept
                </button>
                <button
                  type="button"
                  onClick={() => onReview(s.id, 'rejected')}
                  disabled={reviewBusy === s.id}
                  className="px-3 py-1.5 bg-enf-surface-mid hover:bg-enf-surface-elevated disabled:opacity-50 border border-enf-surface-border text-enf-text-secondary rounded-md text-xs font-semibold flex items-center gap-1.5 transition-colors"
                  data-testid={`suggestion-reject-${s.id}`}
                >
                  <X className="w-3.5 h-3.5" /> Reject
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    )}

    <div className="mt-4 pt-4 border-t border-enf-surface-border text-xs text-enf-text-muted">
      <span className="text-enf-text-secondary font-semibold">Accept</span> = flag for Library promotion (you'll still need to author full instructions + image before it lands in the canonical list).
      {' '}
      <span className="text-enf-text-secondary font-semibold">Reject</span> = mark reviewed without promotion. The exercise stays saved on the user's own list either way.
    </div>
  </div>
);

export default ExerciseSuggestionsCard;
